import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SeoHead from '@/components/SeoHead';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.forgotPassword(email);
      setSent(true);
      toast.success('If that email exists, reset instructions have been sent');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <SeoHead
        title="Forgot Password | JOBFITAI"
        description="Request a password reset email for your JOBFITAI account if you need to regain access."
        canonicalPath="/forgot-password"
        robots="noindex,follow"
      />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex">
            <AppLogo variant="auth" />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white border border-border rounded-xl shadow-soft p-8">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl font-bold mb-2">Forgot password?</h1>
                <p className="text-muted-foreground">Enter your email to receive reset instructions</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-11 pl-10 pr-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      placeholder="you@example.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white h-11 rounded-lg font-medium hover:bg-primary-hover transition-all disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send reset instructions'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-success" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Check your email</h2>
              <p className="text-muted-foreground mb-6">
                If an account exists for {email}, you'll receive password reset instructions.
              </p>
            </div>
          )}

          <Link 
            to="/login" 
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground mt-6"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
